import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Badge,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import ExportCSVButton from '../../../components/report/export/ExportCSVButton';
import { EventLogEntry, ExistingCredential } from '../types';
import { STATUS_LEVEL_MAP, useEventLogs } from '../hooks/useEventLogs';
import EventLogFilterPanel, { OPERATION_LABELS } from './EventLogFilterPanel';
import EventLogDetailRow from './EventLogDetailRow';

interface EventLogsTableProps {
  credentials: ExistingCredential[];
}

// ---------------------------------------------------------------------------

const formatTimestamp = (ts: string): string => {
  const m = moment(ts);
  return m.isValid() ? m.local().format('MMM D, YYYY h:mm:ss A') : ts;
};

const getStatusLevelLabel = (status: number) =>
  STATUS_LEVEL_MAP[String(status).charAt(0)]?.label ?? 'Info';

const formatBody = (raw?: string): string => {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const getCredentialLabel = (log: EventLogEntry, credential?: ExistingCredential): string => {
  if (credential) {
    return credential.description || credential.key;
  }
  return log.thirdPartyId?.substring(0, 8) || '';
};

const getCsvRows = (
  logs: EventLogEntry[],
  credentialMap: Map<string, ExistingCredential>
) => logs.map(log => ({
  level: getStatusLevelLabel(log.responseStatus),
  timestamp: formatTimestamp(log.timestamp),
  operation: OPERATION_LABELS[log.operationId] ?? log.operationId,
  method: log.method,
  path: log.path,
  status: log.responseStatus,
  duration: `${log.durationMs}ms`,
  apiKey: getCredentialLabel(log, credentialMap.get(log.thirdPartyId)),
  clientIp: log.clientIp,
  traceId: log.traceId,
  accountId: log.accountId,
  queryString: log.queryString || '',
  requestBody: formatBody(log.requestBody),
  responseBody: formatBody(log.responseBody),
}));

const csvHeaders = [
  { label: 'Level', key: 'level' },
  { label: 'Timestamp', key: 'timestamp' },
  { label: 'Operation', key: 'operation' },
  { label: 'Method', key: 'method' },
  { label: 'Path', key: 'path' },
  { label: 'Status', key: 'status' },
  { label: 'Duration', key: 'duration' },
  { label: 'API Key', key: 'apiKey' },
  { label: 'Client IP', key: 'clientIp' },
  { label: 'Trace ID', key: 'traceId' },
  { label: 'Account ID', key: 'accountId' },
  { label: 'Query String', key: 'queryString' },
  { label: 'Request Body', key: 'requestBody' },
  { label: 'Response Body', key: 'responseBody' },
];

const toolbarIconButtonSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  color: 'text.secondary',
  width: 40,
  height: 40,
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
};

const primaryToolbarIconButtonSx = {
  ...toolbarIconButtonSx,
  borderColor: 'primary.main',
  color: 'primary.main',
  '&:hover': {
    borderColor: 'primary.dark',
    color: 'primary.dark',
    bgcolor: 'primary.light',
  },
};

const EventLogsTable = ({ credentials }: EventLogsTableProps) => {
  const {
    logs,
    filteredLogs,
    loading,
    error,
    filters,
    showFilters,
    keyword,
    levelFilter,
    activeFilterCount,
    expandedTraceId,
    setExpandedTraceId,
    setShowFilters,
    setKeyword,
    setLevelFilter,
    handleFilterChange,
    handleDateChange,
    resetFilters,
    fetchLogs,
  } = useEventLogs();

  const credentialMap = useMemo(() => new Map(credentials.map(c => [c.id, c])), [credentials]);
  const csvRows = useMemo(
    () => getCsvRows(filteredLogs, credentialMap),
    [filteredLogs, credentialMap]
  );

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>

      {/* ── Header bar ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {/* Keyword search — grows to fill remaining space */}
        <TextField
          placeholder="Search path, operation, trace ID, IP…"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          size="small"
          variant="outlined"
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />

        <ExportCSVButton
          title="Download CSV"
          reportName={`api-integration-logs-${moment().format('YYYY-MM-DD-HHmmss')}.csv`}
          reportData={csvRows}
          csvLinkProps={{ headers: csvHeaders }}
          className=""
          linkClassName=""
          iconOnly
          tooltipTitle="Download CSV"
          size="small"
          disabled={loading || filteredLogs.length === 0}
          sx={primaryToolbarIconButtonSx}
        />

        {/* Filter toggle */}
        <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <IconButton
              size="small"
              onClick={() => setShowFilters(v => !v)}
              sx={{
                ...toolbarIconButtonSx,
                borderColor: showFilters ? 'primary.main' : 'divider',
                color: showFilters ? 'primary.main' : 'text.secondary',
                bgcolor: showFilters ? 'primary.light' : undefined,
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: showFilters ? 'primary.light' : 'action.hover',
                },
              }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Badge>
        </Tooltip>

        {/* Refresh */}
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={fetchLogs}
            disabled={loading}
            sx={toolbarIconButtonSx}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Collapsible filter panel ────────────────────────────────────── */}
      <EventLogFilterPanel
        open={showFilters}
        filters={filters}
        levelFilter={levelFilter}
        credentials={credentials}
        onFilterChange={handleFilterChange}
        onDateChange={handleDateChange}
        onLevelFilterChange={setLevelFilter}
        onReset={resetFilters}
      />

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }} />
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Operation</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>API Key</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client IP</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Loading logs…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                  <SearchIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {keyword.trim() && logs.length > 0
                      ? `No logs match "${keyword}"`
                      : logs.length === 0 && activeFilterCount === 0
                        ? 'No logs found'
                        : 'No logs found for the selected filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map(log => (
                <EventLogDetailRow
                  key={log.traceId}
                  log={log}
                  credential={credentialMap.get(log.thirdPartyId)}
                  isExpanded={expandedTraceId === log.traceId}
                  onToggle={() => setExpandedTraceId(prev => prev === log.traceId ? null : log.traceId)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Footer count ───────────────────────────────────────────────── */}
      {!loading && filteredLogs.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {filteredLogs.length}
          {filteredLogs.length !== logs.length ? ` of ${logs.length}` : ''} result{filteredLogs.length !== 1 ? 's' : ''}
          {logs.length >= filters.size && ' (limit reached — narrow your date range or filters for more specific results)'}
        </Typography>
      )}
    </Grid>
  );
};

export default EventLogsTable;
